import {
    animate,
    AnimationBuilder,
    AnimationMetadata,
    AnimationPlayer,
    style,
} from "@angular/animations";
import {
    Directive,
    effect,
    EmbeddedViewRef,
    inject,
    Input,
    OnDestroy,
    OnInit,
    Renderer2,
    TemplateRef,
    ViewContainerRef,
} from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";

import { map } from "rxjs";

import { BreakpointService } from "@hermes/utils-generic/services/user-interface";

@Directive({
    selector: "[hOnTop]",
    standalone: true,
})
export class DissolveOnTopDirective implements OnInit, OnDestroy {
    @Input({ alias: "hOnTop", required: true })
    public layer!: TemplateRef<unknown>;

    @Input({ required: true })
    public hOnTopPosition!: "fixed" | "absolute";

    private readonly viewContainerRef = inject(ViewContainerRef);
    private readonly templateRef = inject(TemplateRef);
    private readonly renderer = inject(Renderer2);
    private readonly builder = inject(AnimationBuilder);
    private readonly position = toSignal(
        inject(BreakpointService)
            .mediumBreakpointObserver()
            .pipe(map(({ matches }) => (matches ? "absolute" : "fixed"))),
        { initialValue: "fixed" },
    );

    readonly #leaveAnimation: AnimationMetadata[] = [
        style({ opacity: 1 }),
        animate("400ms ease-out", style({ opacity: 0 })),
    ];

    readonly #enterAnimation: AnimationMetadata[] = [
        style({ opacity: 0 }),
        animate("400ms ease-out", style({ opacity: 1 })),
    ];

    #topLayerView: EmbeddedViewRef<unknown> | undefined;
    #enterAnimationPlayer!: AnimationPlayer;
    #leaveAnimationPlayer!: AnimationPlayer;

    /**
     * Insert default layer in the view
     * Prepare parent element to receive absolute top layer
     */
    constructor() {
        this.viewContainerRef.createEmbeddedView(this.templateRef);

        const { parentElement } = this.viewContainerRef.element.nativeElement;
        this.renderer.setStyle(parentElement, "position", "relative");

        effect(() => {
            this.setLayerStyle("position", this.position());
        });
    }

    /**
     * Once the top layer template has been rendered as a view,
     * the top layer element is the first view node
     */
    private get topLayerElement(): HTMLElement {
        return this.#topLayerView?.rootNodes[0];
    }

    @Input({ required: true })
    public set hOnTopTrigger(requested: boolean | undefined) {
        if (!this.#topLayerView || requested === undefined) {
            return;
        }

        if (this.isOpenLayer(requested)) {
            this.viewContainerRef.insert(this.#topLayerView);
            this.#enterAnimationPlayer.play();
            return;
        }

        if (this.isCloseLayer(requested)) {
            this.#leaveAnimationPlayer.play();
        }
    }

    /**
     * Here are memory optimizations
     * - create enbedded view in advance
     * - store viewInstance to insert and detach on demand
     * - position layer on top only once
     * - create player instances to reuse same animations
     * - register players callbacks only once
     */
    public ngOnInit(): void {
        this.#topLayerView = this.layer.createEmbeddedView(undefined);

        this.initEnterAnimationPlayer();
        this.initLeaveAnimationPlayer();
    }
    public ngOnDestroy() {
        this.#enterAnimationPlayer.destroy();
        this.#leaveAnimationPlayer.destroy();
    }

    private initEnterAnimationPlayer() {
        this.#enterAnimationPlayer = this.getPlayer(this.#enterAnimation);

        this.#enterAnimationPlayer.onDone(() => {
            this.#enterAnimationPlayer.reset();
        });
    }

    private initLeaveAnimationPlayer() {
        this.#leaveAnimationPlayer = this.getPlayer(this.#leaveAnimation);

        this.#leaveAnimationPlayer.onDone(() => {
            this.#leaveAnimationPlayer.reset();
            this.viewContainerRef.detach();
        });
    }

    private getPlayer(animation: AnimationMetadata[]) {
        return this.builder.build(animation).create(this.topLayerElement);
    }

    private isOpenLayer(requested: boolean): boolean {
        return requested && this.viewContainerRef.length === 1;
    }

    private isCloseLayer(requested: boolean): boolean {
        return !requested && this.viewContainerRef.length === 2;
    }

    private setLayerStyle(rule: string, value: string) {
        this.renderer.setStyle(this.topLayerElement, rule, value);
    }
}
